/**
 * Golf Data Service — Parallel data strategy
 * Priority: Local DB → GolfAPI (current) → OpenGolfAPI → OSM
 * Silently captures course data for eventual independence from GolfAPI
 */

const LOCAL_DB_KEY = 'golf_courses_v1';
const BAG_KEY = 'golf_bag_v1';

// ============ LOCAL DATABASE ============

export function getLocalCourses() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_DB_KEY) || '{}');
  } catch { return {}; }
}

export function getLocalCourse(id) {
  const db = getLocalCourses();
  return db[id] || null;
}

export function saveLocalCourse(course) {
  const db = getLocalCourses();
  const id = course.course_id || course.id || course.courseID;
  if (!id) return;
  db[id] = { ...course, localSavedAt: Date.now() };
  localStorage.setItem(LOCAL_DB_KEY, JSON.stringify(db));
}

// ============ COURSE NORMALIZER (Adapter Layer) ============

export function normalizeCourse(raw, source) {
  switch (source) {
    case 'golfapi':
      return {
        id: raw.courseID || raw.id,
        clubName: raw.clubName || raw.name,
        courseName: raw.courseName || raw.clubName || '',
        city: raw.city || '',
        state: raw.state || '',
        address: raw.address || '',
        latitude: raw.latitude || raw.lat,
        longitude: raw.longitude || raw.lng,
        distance: raw.distance || 0,
        numHoles: raw.numHoles || raw.holes_count || 18,
        hasGPS: raw.hasGPS || false,
        par: raw.par || raw.par_total,
        courseType: raw.courseType || raw.course_type || '',
        source: 'golfapi',
        raw,
      };
    case 'opengolf':
      return {
        id: raw.id,
        clubName: raw.club_name || raw.course_name || '',
        courseName: raw.course_name || '',
        city: raw.city || '',
        state: raw.state || '',
        address: raw.address || '',
        latitude: raw.latitude,
        longitude: raw.longitude,
        distance: 0,
        numHoles: raw.holes_count || 18,
        hasGPS: false,
        par: raw.par_total,
        courseType: raw.course_type || '',
        yearBuilt: raw.year_built,
        website: raw.website,
        phone: raw.phone,
        scorecard: raw.scorecard || [],
        facilities: raw.facilities,
        source: 'opengolf',
        raw,
      };
    case 'osm':
      return {
        id: `osm_${raw.osm_id}`,
        clubName: raw.name || 'Unknown Course',
        courseName: raw.name || '',
        city: '',
        state: '',
        address: '',
        latitude: raw.latitude,
        longitude: raw.longitude,
        distance: 0,
        numHoles: 18,
        hasGPS: false,
        par: null,
        courseType: '',
        source: 'osm',
        raw,
      };
    default:
      return raw;
  }
}

// ============ SILENT DATA CAPTURE ============

export function captureHoleData(courseId, holeNumber, gpsPosition) {
  const key = `hole_samples_${courseId}`;
  try {
    const samples = JSON.parse(localStorage.getItem(key) || '{}');
    const holeKey = `hole_${holeNumber}`;
    if (!samples[holeKey]) samples[holeKey] = [];
    samples[holeKey].push({
      lat: gpsPosition.lat,
      lng: gpsPosition.lng,
      timestamp: Date.now(),
    });
    // Keep last 20 samples per hole
    if (samples[holeKey].length > 20) samples[holeKey].shift();
    localStorage.setItem(key, JSON.stringify(samples));
  } catch {}
}

// ============ AUTO-REFINEMENT ============

export function refineHoleCenter(courseId, holeNumber) {
  const key = `hole_samples_${courseId}`;
  try {
    const samples = JSON.parse(localStorage.getItem(key) || '{}');
    const holeSamples = samples[`hole_${holeNumber}`] || [];
    if (holeSamples.length < 5) return null;
    const lat = holeSamples.reduce((sum, s) => sum + s.lat, 0) / holeSamples.length;
    const lng = holeSamples.reduce((sum, s) => sum + s.lng, 0) / holeSamples.length;
    return { lat, lng, sampleCount: holeSamples.length };
  } catch { return null; }
}

// ============ CONFIDENCE SCORE ============

export function calculateConfidence(course) {
  let score = 0;
  if (course?.holes?.length) score += 30;
  if (course?.source === 'user' || course?.source === 'local') score += 30;
  if (course?.scorecard?.length) score += 15;
  if (course?.par) score += 5;
  if (course?.latitude && course?.longitude) score += 10;
  // Check local samples
  const key = `hole_samples_${course?.id || course?.course_id}`;
  try {
    const samples = JSON.parse(localStorage.getItem(key) || '{}');
    const totalSamples = Object.values(samples).reduce((sum, arr) => sum + arr.length, 0);
    if (totalSamples > 10) score += 20;
    else if (totalSamples > 5) score += 10;
  } catch {}
  return Math.min(score, 100);
}

// ============ VIRTUAL GOLF BAG (Local) ============

const DEFAULT_BAG = [
  { name: 'Driver', abbr: 'DR', avg_distance: 230 },
  { name: '3 Wood', abbr: '3W', avg_distance: 210 },
  { name: '5 Wood', abbr: '5W', avg_distance: 195 },
  { name: '4 Hybrid', abbr: '4H', avg_distance: 185 },
  { name: '5 Iron', abbr: '5i', avg_distance: 175 },
  { name: '6 Iron', abbr: '6i', avg_distance: 165 },
  { name: '7 Iron', abbr: '7i', avg_distance: 155 },
  { name: '8 Iron', abbr: '8i', avg_distance: 145 },
  { name: '9 Iron', abbr: '9i', avg_distance: 130 },
  { name: 'Pitching Wedge', abbr: 'PW', avg_distance: 115 },
  { name: 'Sand Wedge', abbr: 'SW', avg_distance: 90 },
  { name: 'Lob Wedge', abbr: 'LW', avg_distance: 70 },
  { name: 'Putter', abbr: 'P', avg_distance: 0 },
];

export function getLocalBag() {
  try {
    const bag = JSON.parse(localStorage.getItem(BAG_KEY));
    return bag || DEFAULT_BAG;
  } catch { return DEFAULT_BAG; }
}

export function saveLocalBag(clubs) {
  localStorage.setItem(BAG_KEY, JSON.stringify(clubs));
}

export function suggestClubFromBag(yards) {
  if (!yards || yards <= 0) return null;
  const bag = getLocalBag().filter(c => c.avg_distance > 0);
  bag.sort((a, b) => a.avg_distance - b.avg_distance);
  // Find closest club
  let best = bag[0];
  for (const club of bag) {
    if (Math.abs(club.avg_distance - yards) < Math.abs(best.avg_distance - yards)) {
      best = club;
    }
  }
  return best;
}
